import { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Box, CircularProgress, IconButton, Tab, Tabs } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import { useUserState } from "../../state/user";
import { fetchUtente } from "../../services/user";
import { semanticColors } from "../../utils/colors";
import { typography } from "../../utils/typography";
import {
  useClinicalRecords,
  ClinicalRecord,
  Privacy
} from "../../hooks/useClinicalRecords";
import { RegistryType } from "../../services/clinicalRegistries";
import useFichaClinica from "../../hooks/useFichaClinica";
import { doctorTitle } from "../../utils/doctor";
import { formatAge } from "../../components/ClinicalRecords/utils";
import ConfirmDeleteDialog from "../../components/ClinicalRecords/ConfirmDeleteDialog";
import PatientSidebar from "../../components/ClinicalRecords/PatientSidebar";
import RecordsList from "../../components/ClinicalRecords/RecordsList";
import RecordEditor from "../../components/ClinicalRecords/RecordEditor";

type FormView = "list" | "add" | "edit";

const pageSx = {
  bgcolor: semanticColors.surface.canvas,
  minHeight: "100vh"
};

const stickyBarSx = {
  bgcolor: semanticColors.surface.canvas,
  boxShadow: "0px 4px 6px rgba(30, 39, 79, 0.1)",
  position: "sticky",
  top: 80,
  zIndex: 10
};

const topBarSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: { xs: "16px", sm: "24px" },
  minHeight: 47
};

const backBtnSx = { color: semanticColors.text.primary };

const topTabsSx = {
  "& .MuiTabs-indicator": {
    backgroundColor: semanticColors.action.primary,
    height: "2px"
  },
  minHeight: 47
};

const bodySx = {
  maxWidth: "1100px",
  mx: "auto",
  px: "32px",
  pt: "32px",
  pb: "80px",
  display: "flex",
  gap: "32px",
  alignItems: "flex-start",
  minWidth: "660px"
};

const rightColSx = { flex: 1, minWidth: 0 };

const innerTabsSx = {
  mb: "20px",
  "& .MuiTabs-indicator": {
    backgroundColor: semanticColors.action.primary,
    height: "2px"
  },
  "& .MuiTab-root": { minHeight: 40, py: 0 }
};

export default function ClinicalRecordsPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const location = useLocation<{ patient?: any }>();
  const { user, token } = useUserState();

  const [patient, setPatient] = useState<any>(location.state?.patient ?? null);
  const [loadingPatient, setLoadingPatient] = useState(
    !location.state?.patient
  );
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // On normal navigation the patient arrives via location.state. On a directly
  // opened link (fresh tab / shared URL) it isn't there, and /api/patients/:id
  // doesn't return the sidebar data (name, gender, picture…). The clinical-files
  // list does — and it's the same data the Utentes list shows — so use it as the
  // source, falling back to whatever fetchUtente returned. It also carries the
  // clinicalFileId a registry has to be created against.
  const { data: clinicalFiles, loading: filesLoading } = useFichaClinica();
  const fileEntry = Array.isArray(clinicalFiles)
    ? (clinicalFiles as any[]).find(
        file => String(file.nhsId ?? file._id) === id
      )
    : undefined;
  const effectivePatient = patient
    ? { ...patient, pictureUrl: patient.pictureUrl ?? fileEntry?.pictureUrl }
    : fileEntry ?? null;
  const sidebarLoading = !effectivePatient && (loadingPatient || filesLoading);

  const {
    notes,
    summaries,
    loading: recordsLoading,
    fetchRecord,
    addRecord,
    updateRecord,
    deleteRecord
  } = useClinicalRecords({
    nhsId: id,
    clinicalFileId: fileEntry?.clinicalFileId
  });

  // The entry being edited is loaded on its own via a single GET so its content
  // is fresh, falling back to the list entry while that request is in flight.
  const [editingRecord, setEditingRecord] = useState<ClinicalRecord | null>(
    null
  );

  // The inner tab and the open editor are kept in the URL query so the browser
  // back button steps back through them instead of leaving the page:
  //   ?tab=notes|summaries   ?record=new|<id>
  const params = new URLSearchParams(location.search);
  const activeTab = params.get("tab") === "summaries" ? 1 : 0;
  const recordParam = params.get("record");

  const isNotes = activeTab === 0;
  const editing = editingRecord;
  // "edit" only once its record has loaded; otherwise fall back to the list.
  const formView: FormView =
    recordParam === "new" ? "add" : editing ? "edit" : "list";

  // Pushes a new query (tab/record) onto the same page, preserving whichever
  // part isn't being changed. `record: null` closes the editor.
  const setQuery = (next: { tab?: number; record?: string | null }) => {
    const p = new URLSearchParams(location.search);
    if (next.tab !== undefined) {
      // Notes is the default tab, so keep it out of the URL — only Summaries
      // needs a param. This keeps the notes URL identical whether you just
      // arrived or switched back to it.
      if (next.tab === 1) p.set("tab", "summaries");
      else p.delete("tab");
    }
    if (next.record === null) p.delete("record");
    else if (next.record !== undefined) p.set("record", next.record);
    history.push({ pathname: location.pathname, search: p.toString() });
  };

  useEffect(() => {
    if (patient || !token || !id) return;
    (async () => {
      try {
        const data = await fetchUtente(token, id);
        setPatient(data);
      } catch {
        // fall back to empty state
      } finally {
        setLoadingPatient(false);
      }
    })();
  }, [token, id, patient]);

  const title = doctorTitle(user?.personalInfo?.gender, t);
  const doctorName = `${title} ${user?.clinicalName ||
    user?.name ||
    ""}`.trim();
  const doctorSpecialty =
    user?.specialties?.[0] ||
    user?.specialty ||
    user?.professionalData?.specialty ||
    "";
  const doctorPicture = user?.personalInfo?.picture || "";

  // Author identity is display-only; clinical files aren't shared yet, so every
  // entry returned for this patient is the current professional's.
  const withDoctor = (record: ClinicalRecord): ClinicalRecord => ({
    ...record,
    doctorName,
    doctorSpecialty,
    doctorPictureUrl: doctorPicture
  });

  const activeType: RegistryType = isNotes
    ? RegistryType.NOTE
    : RegistryType.SUMMARY;
  const activeRecords = (isNotes ? notes : summaries).map(withDoctor);

  // Load the opened entry on its own (single GET), seeding from the list entry
  // while the request is in flight.
  useEffect(() => {
    if (!recordParam || recordParam === "new") {
      setEditingRecord(null);
      return;
    }
    let cancelled = false;
    const local =
      [...notes, ...summaries].find(record => record.id === recordParam) ??
      null;
    setEditingRecord(local);
    fetchRecord(recordParam)
      .then(record => {
        if (!cancelled && record) setEditingRecord(record);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // notes/summaries only seed the initial value; don't re-fetch on refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordParam, fetchRecord]);

  const handleEdit = (record: ClinicalRecord) => {
    setQuery({ record: record.id });
  };

  const handleCancel = () => {
    setQuery({ record: null });
  };

  const handleSubmit = async (
    content: string,
    privacy: Privacy,
    visibleToPatient: boolean
  ) => {
    try {
      if (formView === "add") {
        await addRecord({
          type: activeType,
          content,
          privacy,
          visibleToPatient
        });
      } else if (formView === "edit" && editing) {
        await updateRecord(editing.id, { content, privacy, visibleToPatient });
      }
      setQuery({ record: null });
    } catch {
      // keep the editor open so the entry isn't lost on a failed request
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteRecord(deleteTarget);
      setDeleteTarget(null);
      setQuery({ record: null });
    } catch {
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  // Switching tab also closes any open editor.
  const handleTabChange = (_: any, val: number) => {
    setQuery({ tab: val, record: null });
  };

  const privacyOptions: Array<{ value: Privacy; label: string }> = [
    { value: "only-me", label: t("clinicalRecords.privacyOnlyMe") },
    { value: "colleagues", label: t("clinicalRecords.privacyColleagues") },
    {
      value: "health-professionals",
      label: t("clinicalRecords.privacyHealthProfessionals")
    }
  ];

  const age = effectivePatient ? formatAge(effectivePatient.birthDate) : "";
  const genderLabel =
    effectivePatient?.gender === 1
      ? t("utentes.male")
      : effectivePatient?.gender === 2
      ? t("utentes.female")
      : "";

  const tabIndicatorColor = semanticColors.action.primary;
  const tabSx = (active: boolean) => ({
    minHeight: 40,
    py: 0,
    ...typography.body.m,
    fontWeight: active ? 600 : 400,
    color: active ? tabIndicatorColor : semanticColors.text.secondary,
    textTransform: "none" as const,
    "&.Mui-selected": { color: tabIndicatorColor }
  });

  return (
    <Box sx={pageSx}>
      <ConfirmDeleteDialog
        open={deleteTarget !== null}
        isNote={isNotes}
        deleting={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Sticky top bar */}
      <Box sx={stickyBarSx}>
        <Box sx={topBarSx}>
          <IconButton
            size="small"
            onClick={() => history.push("/patients")}
            sx={backBtnSx}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Tabs value={0} sx={topTabsSx}>
            <Tab label={t("clinicalRecords.pageTitle")} sx={tabSx(true)} />
          </Tabs>
        </Box>
      </Box>

      {/* Two-column body */}
      <Box sx={bodySx}>
        <PatientSidebar
          loadingPatient={sidebarLoading}
          patient={effectivePatient}
          genderLabel={genderLabel}
          age={age}
        />

        {/* Right: records / form */}
        <Box sx={rightColSx}>
          <Tabs value={activeTab} onChange={handleTabChange} sx={innerTabsSx}>
            <Tab
              label={t("clinicalRecords.tabNotes")}
              sx={tabSx(activeTab === 0)}
            />
            <Tab
              label={t("clinicalRecords.tabSummaries")}
              sx={tabSx(activeTab === 1)}
            />
          </Tabs>

          {formView === "list" ? (
            recordsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <RecordsList
                activeRecords={activeRecords}
                isNotes={isNotes}
                onEdit={handleEdit}
                onAdd={() => setQuery({ record: "new" })}
              />
            )
          ) : (
            <RecordEditor
              formView={formView as "add" | "edit"}
              isNotes={isNotes}
              editing={editing}
              privacyOptions={privacyOptions}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              onDeleteRequest={() => editing && setDeleteTarget(editing.id)}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
